import { rtkq, RtkqRequest } from '../rtkq';
import { api } from '../api';
import {
  AddExternalDocumentDto,
  ExternalDocumentDto,
  externalDocumentsHttp,
  UpdateExternalDocumentDto,
} from '@/core';

export type ExternalDocumentState = ExternalDocumentDto;
export type AddExternalDocumentArgs = AddExternalDocumentDto;
export type UpdateExternalDocumentArgs = UpdateExternalDocumentDto;

const externalDocumentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getExternalDocumentList: build.query<
      ExternalDocumentState[],
      RtkqRequest<{ referenceIds: string[] | null }>
    >({
      queryFn: async (args) => {
        return rtkq(args).exec(() => externalDocumentsHttp.getAll(args));
      },
      providesTags: (result) => [
        { type: 'external-document', id: 'list' },
        ...(result || []).map(({ id }) => ({
          type: 'external-document' as const,
          id,
        })),
      ],
    }),

    getExternalDocumentDetails: build.query<
      ExternalDocumentState,
      RtkqRequest<{ id: string }>
    >({
      queryFn: async (args) => {
        const { id } = args;
        return await rtkq(args).exec(() => externalDocumentsHttp.get(id));
      },
      providesTags: (article) => [
        { type: 'external-document', id: article?.id ?? 'none' },
      ],
    }),

    updateExternalDocument: build.mutation<
      void,
      RtkqRequest<UpdateExternalDocumentArgs>
    >({
      queryFn: (args) => {
        return rtkq(args).exec(() => externalDocumentsHttp.update(args));
      },
      invalidatesTags: (_, __, args) => [
        { type: 'external-document', id: 'list' },
        { type: 'external-document', id: args.id },
      ],
    }),

    addExternalDocument: build.mutation<
      string,
      RtkqRequest<AddExternalDocumentArgs>
    >({
      queryFn: (args) => {
        return rtkq(args).exec(() => externalDocumentsHttp.create(args));
      },
      invalidatesTags: () => [{ type: 'external-document', id: 'list' }],
    }),

    deleteExternalDocument: build.mutation<void, RtkqRequest<{ id: string }>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => externalDocumentsHttp.delete(args.id));
      },
      invalidatesTags: () => [{ type: 'external-document', id: 'list' }],
    }),
  }),
});

export const {
  useAddExternalDocumentMutation,
  useUpdateExternalDocumentMutation,
  useDeleteExternalDocumentMutation,
  useGetExternalDocumentDetailsQuery,
  useGetExternalDocumentListQuery,
} = externalDocumentsApi;
